/**  based and modified off of tmcw's excellent https://github.com/placemark/placemark 
 * 
 * @license MIT License

Copyright (c) 2023 Placemark

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

import { memo, useState, useEffect, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { getFilesFromDataTransferItems } from "@placemarkio/flat-drop-files";
import type { FileWithHandle } from "browser-fs-access";
import {
  FileGroup,
  FileGroups,
  groupFiles,
  ShapefileGroup,
} from "src/lib/group_files";
import { Dialog, DialogContent, DialogTrigger } from "./base/Dialog";
import {
  DEFAULT_IMPORT_OPTIONS,
  detectType,
  FILE_TYPES,
  fileToGeoJSON,
  findType,
} from "src/lib/geo/convert";
import { Shapefile } from "src/lib/geo/convert/shapefile";

import { nanoid } from "@reduxjs/toolkit";
import { getGeoJSONInfo, getStyle } from "./fetchGeoJSON";
import { useAppDispatch } from "src/hooks";
import { actions as MapActions } from "../slices/maps";
import { List, ListItem, ListItemLabel } from "./base/List";
import { Badge } from "./base/Badge";
import { cn, isDefined } from "src/lib/utils";
import { Button } from "./base/button2";
import { GeoTiffLoadSourceOptions } from "./CreateLayer/GeoTiffBuilder";
import { isArray, isEmpty } from "lodash-es";
import { toast } from "react-toastify";
import { Wave } from "src/vendor/better-react-spinkit";
import { Checkbox } from "./base/Checkbox";
import {
  detectColumns,
  csvToGeoJSON,
} from "src/lib/geo/convert/local/csv_to_geojson";
import { dsvFormat } from "d3";

const getCSVImportOptions = async (file: File) => {
  const chunk = await file.slice(0, 4096).text();
  const parser = dsvFormat(",");
  const originalHeaders = parser.parseRows(chunk)[0];

  // Pass both original and lowercase headers to detection
  const csvOptions = detectColumns(originalHeaders.map((h) => h.trim()));

  return {
    ...DEFAULT_IMPORT_OPTIONS,
    type: "csv" as const,
    csvOptions,
  };
};

function createCZMLLayer(name: string, url: string) {
  return {
    type: "czml" as QM.LayerLayer["type"],
    id: nanoid(5),
    opacity: 1,
    url,
    projections: [],
    name,
    label: name,
    enabled: true,
    visible: true,
    options: {
      canDelete: true,
    },
    //defaultExtent:
  };
}

function createGeoJSONLayer(name: string, geojson: GeoJSON.FeatureCollection) {
  const geojsonInfo = getGeoJSONInfo(geojson);
  const layer = {
    id: nanoid(5),
    opacity: 1,
    options: {
      olLayer: { declutter: false },
      cesiumLayer: { clampToGround: !geojsonInfo.hasHeight },
      searchFields: geojsonInfo.searchFields,
      canDelete: true,
    },
    url: URL.createObjectURL(
      new Blob([JSON.stringify(geojson)], {
        type: "application/json",
      })
    ),
    style: {
      ...getStyle(),
      labelProperty: geojsonInfo.labelProperty,
    },
    projections: [],
    name,
    label: name,
    type: "vector" as QM.LayerLayer["type"],
    enabled: true,
    visible: true,
    defaultExtent: geojsonInfo.bbox,
  };
  return layer;
}
const DEFAULT_GEOTIFF_LAYER_OPTIONS: Partial<QM.LayerLayer> = {
  type: "geotiff" as QM.LayerLayer["type"],
  enabled: true,
  visible: true,
  opacity: 1,
  band: 1,
  rangePalette: "wcolut0",
  options: { canDelete: true },
  projections: [],
};
function createGeoTIFFLayer(name: string, source: GeoTiffLoadSourceOptions) {
  return {
    ...DEFAULT_GEOTIFF_LAYER_OPTIONS,
    id: nanoid(5),
    name,
    label: name,
    sources: [source],
  };
}
/**
 * From an event, get files, with handles for re-saving.
 * Result is nullable.
 */

const stopWindowDrag = (event: DragEvent) => {
  event.preventDefault();
};
function StyledDropOverlay({
  children,
}: React.PropsWithChildren<Record<string, unknown>>) {
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-secondary/75 pointer-events-none">
      <div className="px-3 py-2 text-secondary-foreground rounded-md max-w-36">
        {children}
      </div>
    </div>
  );
}

const import_types = new WeakMap<
  FileGroup | ShapefileGroup,
  Awaited<ReturnType<typeof detectType>>
>();
export default memo(function ImportFilesDrop({
  mapId,
  displayProjection,
}: {
  mapId: string;
  displayProjection: QM.Projection;
}) {
  const dispatch = useAppDispatch();
  const [dragging, setDragging] = useState<boolean>(false);
  const [files, setFiles] = useState<FileGroups>();
  const [isImporting, setIsImporting] = useState(false);
  const [showUnident, setShowUnident] = useState(false);
  useEffect(() => {
    const onDropFiles = async (files: FileWithHandle[]) => {
      if (!files.length) return;
      const grouped = groupFiles(files);
      await Promise.all(
        grouped.map(async (g) => {
          try {
            const type =
              g.type === "shapefile"
                ? await detectType(g.files.shp)
                : await detectType(g.file);
            import_types.set(g, type);
          } catch (e) {
            // eat exception
          }
        })
      );
      setFiles(grouped);
    };

    const onDragEnter = () => {
      setDragging(true);
    };

    const onDragLeave = (event: DragEvent) => {
      if (!event.relatedTarget) {
        setDragging(false);
        return;
      }
      const portals = document.querySelectorAll("[data-radix-portal]");
      for (const portal of portals) {
        if (
          event.relatedTarget instanceof Node &&
          portal.contains(event.relatedTarget)
        ) {
          setDragging(false);
          return;
        }
      }
    };

    const onDrop = async (event: DragEvent) => {
      setDragging(false);
      const files = event.dataTransfer?.items
        ? await getFilesFromDataTransferItems(event.dataTransfer.items)
        : [];
      onDropFiles(files);
      event.preventDefault();
    };

    const onDropCaught = (event: DragEvent) => {
      onDrop(event).catch((e) => Sentry.captureException(e));
    };

    document.addEventListener("dragenter", onDragEnter);
    document.addEventListener("dragleave", onDragLeave);
    document.addEventListener("drop", onDropCaught);
    window.addEventListener("dragover", stopWindowDrag);
    window.addEventListener("drop", stopWindowDrag);

    return () => {
      document.removeEventListener("dragenter", onDragEnter);
      document.removeEventListener("dragleave", onDragLeave);
      document.removeEventListener("drop", onDropCaught);
      window.removeEventListener("dragover", stopWindowDrag);
      window.removeEventListener("drop", stopWindowDrag);
    };
  }, [setDragging]);

  const handleImport = useCallback(async () => {
    if (isArray(files) && !isEmpty(files)) {
      setIsImporting(true);

      const importableFiles = files.filter(
        (file) =>
          (file.type === "file" && isDefined(import_types.get(file))) ||
          file.type === "shapefile"
      );
      const defaultVisible = importableFiles.length < 4;
      await Promise.all(
        files?.map(async (file) => {
          let result;
          let filename;
          try {
            if (file.type === "file") {
              filename = file.file.name;
              let options = await detectType(file.file).catch(() => null);
              if (!options) return;

              if (options.type === "csv") {
                options = await getCSVImportOptions(file.file);
              }
              if (options.type === "czml") {
                // use czml loader
                const layer = createCZMLLayer(
                  filename,
                  URL.createObjectURL(file.file)
                );
                console.log("loading czml", filename, layer);
                if (!defaultVisible) {
                  layer.visible = false;
                }
                dispatch(MapActions.addLayerToMap(mapId, layer));

                return;
              } else if (options.type === "geotiff") {
                // use geotiff loader
                const layer = createGeoTIFFLayer(filename, {
                  blob: file.file,
                });
                if (!defaultVisible) {
                  layer.visible = false;
                }
                dispatch(MapActions.addLayerToMap(mapId, layer));

                return;
              } else {
                // Handle other file types with default options
                result = await fileToGeoJSON(
                  await file.file.arrayBuffer(),
                  { ...options, lonlatProjection: displayProjection.proj },
                  //@ts-ignore
                  () => {}
                );
                if ("geojson" in result) {
                  const layer = createGeoJSONLayer(filename, result.geojson);
                  if (!defaultVisible) {
                    layer.visible = false;
                  }
                  dispatch(MapActions.addLayerToMap(mapId, layer));
                  return;
                }
              }
            } else {
              filename = file.files.shp.name;
              result = await Shapefile.forwardLoose(file, {
                ...DEFAULT_IMPORT_OPTIONS,
                type: "shapefile",
                lonlatProjection: displayProjection.proj,
              });
              if (result) {
                const layer = createGeoJSONLayer(filename, result);
                if (!defaultVisible) {
                  layer.visible = false;
                }
                dispatch(MapActions.addLayerToMap(mapId, layer));
                return;
              }
            }
          } catch (e) {
            console.log("error", e);
            toast.error(`Failed to import ${filename}: ${e.message}`);
          }
        })
      );
      setIsImporting(false);
      setFiles(undefined);
    }
  }, [files, displayProjection, mapId]);
  if (dragging) {
    return <StyledDropOverlay>Drop files to add to the map</StyledDropOverlay>;
  }
  if (files) {
    return (
      <Dialog open={true} onOpenChange={() => setFiles(undefined)}>
        <DialogTrigger></DialogTrigger>
        <DialogContent className="max-h-[80vh]">
          <h3>Import Files</h3>
          <List className="flex-col overflow-y-auto max-h-60">
            {files.map((file) => {
              if (file.type === "file") {
                let type = import_types.get(file)?.type;
                if (!showUnident && !isDefined(type)) return null;
                return (
                  <ListItem>
                    <ListItemLabel
                      className={cn("text-xs", {
                        "opacity-50": !isDefined(type),
                      })}
                    >
                      {file.file.name}{" "}
                      <Badge className="ml-auto">{type ?? `unknown`}</Badge>
                    </ListItemLabel>
                  </ListItem>
                );
              } else {
                return (
                  <ListItem>
                    <ListItemLabel className={cn("text-xs")}>
                      {file.files.shp.name}{" "}
                      <Badge className="ml-auto">
                        {import_types.get(file)?.type ?? file.type}
                      </Badge>
                    </ListItemLabel>
                    <></>
                  </ListItem>
                );
              }
            })}
          </List>
          <div className="flex items-center text-xs space-x-2 justify-end">
            <Checkbox
              id="showUnident"
              checked={showUnident}
              onCheckedChange={(checked) => setShowUnident(!!checked)}
            />
            <label className="select-none" htmlFor="showUnident">
              Show unsupported files
            </label>
          </div>
          <Button
            variant="cta"
            onClick={() => handleImport()}
            disabled={isEmpty(files) || isImporting}
          >
            Import{" "}
            {isImporting && (
              <span className="ml-2">
                <Wave size={16} color="white" />
              </span>
            )}
          </Button>
        </DialogContent>
      </Dialog>
    );
  }
  return null;
});

function Import({ files }: { files: FileGroups }) {}
